import { zodResolver } from '@hookform/resolvers/zod';
import { Bell, Building2, Calendar, FileText, Globe, Plus, ShieldCheck, Users } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { useCurrentUserCustomer, useUpdateCustomer } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { UserManagement } from '@/features/shared/users';
import { CustomerDetailsFields } from '@/shared/components/customer/customer-details-fields';
import { CustomerQuotaCard } from '@/shared/components/customer/customer-quota-card';
import { ObjectReminderSettingsCard } from '@/shared/components/customer/object-reminder-settings-card';
import { DetailCard, DetailPageLayout, TabNav } from '@/shared/components/layout';
import { Button, Form, FormLabel, Input } from '@/shared/components/ui';
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard';
import {
  CUSTOMER_ADMIN_ASSIGNABLE_GROUP_KEYS,
  HELI_PROVIDER_GROUP_KEY,
  isCustomerManagerGroup,
} from '@/shared/utils/permissions';

const objectDigestIntervalSchema = z
  .preprocess(
    (value) => (value === '' ? undefined : value),
    z.coerce.number().min(1).max(24).optional()
  )
  .optional();

const customerFormSchema = z.object({
  name: z.string().min(1),
  companyType: z.string().optional(),
  industry: z.string().optional(),
  website: z.string().url().optional().or(z.literal('')),
  objectDigestEnabled: z.boolean().optional(),
  objectDigestRecipients: z.array(z.string()).optional(),
  objectDigestInterval: objectDigestIntervalSchema,
});

type CustomerFormValues = z.infer<typeof customerFormSchema>;

export function ManageCustomer() {
  const { t } = useIntl();
  const location = useLocation();
  const [usage, setUsage] = useState({ userCount: 0, inviteCount: 0 });
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const { data: customer } = useCurrentUserCustomer();
  const updateCustomer = useUpdateCustomer();
  const { user: currentUser } = useAuth();
  const initialValuesRef = useRef<CustomerFormValues | null>(null);

  const form = useForm<z.input<typeof customerFormSchema>, any, CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      name: '',
      companyType: '',
      industry: '',
      website: '',
      objectDigestEnabled: false,
      objectDigestRecipients: [],
      objectDigestInterval: 6,
    },
  });

  useEffect(() => {
    if (customer) {
      const nextValues: CustomerFormValues = {
        name: customer.name,
        companyType: customer.companyType || '',
        industry: customer.industry || '',
        website: customer.website || '',
        objectDigestEnabled: customer.objectDigestEnabled ?? false,
        objectDigestRecipients: (customer.objectDigestRecipients ?? []) as string[],
        objectDigestInterval: customer.objectDigestInterval ?? 6,
      };
      form.reset(nextValues);
      initialValuesRef.current = nextValues;
    }
  }, [customer, form]);

  const discardChanges = () => {
    if (initialValuesRef.current) {
      form.reset(initialValuesRef.current);
    }
  };

  const { dialog: unsavedDialog } = useUnsavedChangesGuard({
    when: form.formState.isDirty,
    onDiscard: discardChanges,
  });

  const handleSubmit = async (data: CustomerFormValues) => {
    if (!customer || !isCustomerManagerGroup(currentUser)) return;
    if (data.objectDigestEnabled && (data.objectDigestRecipients ?? []).length === 0) {
      form.setError('objectDigestRecipients', {
        type: 'manual',
        message: t('customer.objectDigest.recipientsRequired'),
      });
      return;
    }
    if (
      data.objectDigestEnabled &&
      (typeof data.objectDigestInterval !== 'number' ||
        Number.isNaN(data.objectDigestInterval) ||
        data.objectDigestInterval < 1 ||
        data.objectDigestInterval > 24)
    ) {
      form.setError('objectDigestInterval', {
        type: 'manual',
        message: t('customer.objectDigest.intervalInvalid'),
      });
      return;
    }
    try {
      await updateCustomer.mutateAsync({ id: customer.id, data });
      toast.success(t('customer.customerUpdatedSuccessfully'));
    } catch (_error) {
      form.setError('root', {
        type: 'manual',
        message: t('errors.unknownError'),
      });
    }
  };

  const customerType = customer?.customerType ?? 'customer';
  const isHeliProviderCustomer = customerType === 'heli-provider';
  const canEditCustomer = isCustomerManagerGroup(currentUser);
  const allowedGroupKeys = isHeliProviderCustomer
    ? [HELI_PROVIDER_GROUP_KEY]
    : CUSTOMER_ADMIN_ASSIGNABLE_GROUP_KEYS;
  const userQuota = customer?.userQuota ?? 0;
  const totalUsage = usage.userCount + usage.inviteCount;
  const handleUsageChange = useCallback((nextUsage: typeof usage) => {
    setUsage(nextUsage);
  }, []);

  const metaItems = customer
    ? [
        ...(customer.industry ? [{ icon: Building2, label: customer.industry }] : []),
        ...(customer.website
          ? [
              {
                icon: Globe,
                label: customer.website.replace('https://', ''),
                href: customer.website,
              },
            ]
          : []),
        ...(customer.createdAt
          ? [
              {
                icon: Calendar,
                label: `${t('common.since')} ${DateTime.fromISO(customer.createdAt).toFormat('dd.MM.yyyy')}`,
              },
            ]
          : []),
      ]
    : undefined;

  const avatarContent = customer ? (
    <span className="text-3xl font-bold">{customer.name.substring(0, 2).toUpperCase()}</span>
  ) : undefined;

  const rawTab = location.pathname.split('/').pop() || 'general';
  const currentTab = rawTab === 'customer' ? 'general' : rawTab;
  const tabs = [
    { value: 'general', label: t('common.general'), icon: FileText },
    { value: 'users', label: t('users.users'), icon: Users },
    { value: 'notifications', label: t('customer.objectDigest.title'), icon: Bell },
  ];
  const isGeneralTab = currentTab === 'general';
  const isNotificationsTab = currentTab === 'notifications';
  const isUsersTab = currentTab === 'users';

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        title: customer?.name ?? t('customer.customer'),
        avatarContent,
        metaItems,
        subtitle: !customer ? t('common.loading') : undefined,
        primaryAction: {
          label: updateCustomer.isPending ? t('common.saving') : t('common.save'),
          onClick: form.handleSubmit(handleSubmit),
          disabled: updateCustomer.isPending || !canEditCustomer,
          loading: updateCustomer.isPending,
        },
      }}
    >
      <TabNav tabs={tabs} basePath="/c/my-customer/customer" variant="underline" />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          {isGeneralTab && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <DetailCard icon={Building2} title={t('customers.customerDetails')}>
                  <CustomerDetailsFields
                    control={form.control}
                    disabled={!canEditCustomer}
                    labels={{
                      name: t('common.name'),
                      companyType: t('customer.companyType'),
                      industry: t('customer.industry'),
                      website: t('customer.website'),
                    }}
                    placeholders={{
                      name: t('customers.companyNamePlaceholder'),
                      companyType: t('customers.companyTypePlaceholder'),
                      industry: t('customers.industryPlaceholder'),
                      website: 'https://example.com',
                    }}
                    websiteWithIcon
                  />
                </DetailCard>
              </div>

              <div className="space-y-6">
                <CustomerQuotaCard
                  icon={ShieldCheck}
                  title={t('customers.licenseAndQuota')}
                  description={t('customer.userQuotaReadOnlyDescription')}
                  usageLabel={t('customers.userLicenses')}
                  totalUsage={totalUsage}
                  quota={userQuota}
                  usageDetails={t('customers.userQuotaUsageDetails', {
                    userCount: usage.userCount,
                    inviteCount: usage.inviteCount,
                  })}
                  quotaControl={
                    <div className="space-y-2">
                      <FormLabel>{t('customer.userQuotaLabel')}</FormLabel>
                      <Input value={userQuota || ''} readOnly disabled />
                    </div>
                  }
                />
              </div>
            </div>
          )}

          {isNotificationsTab && (
            <ObjectReminderSettingsCard
              control={form.control}
              disabled={!canEditCustomer}
              customerId={customer?.id}
              lastSentAt={customer?.objectDigestLastSentAt}
              enabledAt={customer?.objectDigestEnabledAt}
              customerType={customer?.customerType}
            />
          )}
        </form>
      </Form>

      <DetailCard
        icon={Users}
        title={t('users.users')}
        noPadding
        className={isUsersTab ? 'mt-8' : 'mt-8 hidden'}
        headerAction={
          canEditCustomer && (
            <Button variant="outline" size="sm" onClick={() => setIsInviteDialogOpen(true)}>
              <Plus className="size-4 mr-2" />
              {t('users.inviteUser')}
            </Button>
          )
        }
      >
        <UserManagement
          allowedGroupKeys={[...allowedGroupKeys]}
          hideInviteButton
          onUsageChange={handleUsageChange}
          embedded
          isInviteDialogOpen={isInviteDialogOpen}
          onInviteDialogOpenChange={setIsInviteDialogOpen}
        />
      </DetailCard>

      {unsavedDialog}
    </DetailPageLayout>
  );
}
